import { useCallback, useEffect, useRef, useState } from 'react';

/**
 * The criminal-practice journey animation, framed in the landing hero.
 *
 * The animation is an exported page (public/journey/criminal.html) that
 * draws on a FIXED 1920x1080 stage — it does not scale to its window, so a
 * plain iframe crops it to the top-left corner. It is rendered at full size
 * and scaled down to the frame's width instead; the frame keeps 16:9.
 *
 * It needs its own CSP (nginx `location ^~ /journey/`): its loader unpacks
 * its parts into blob: URLs, which the site policy forbids.
 *
 * It is a 470 KB bundle that unpacks itself, and until it has, the frame
 * shows the bundle's own blank page with a 9px "Loading…" (owner, 2026-10-26:
 * "taking time to load"). So the frame stays on a dark placeholder that
 * belongs to the hero, and the animation fades in over it once the bundle
 * has really put its stage up — it appears, it is never seen loading. The
 * download itself starts at once (no `loading="lazy"` — the frame is above
 * the fold at the only width it renders) and is cacheable (nginx).
 */
export const JOURNEY_SRC = '/journey/criminal.html';
const STAGE_W = 1920;
const STAGE_H = 1080;
// Where the stages sit on the placeholder rail (% of the frame, 12 -> 88).
const STAGE_STOPS = [12, 31, 50, 69, 88];

// The bundle's loader leaves this node in the page until it has replaced the
// document with the real stage; its absence + a populated body = ready.
const LOADER_ID = '__bundler_placeholder';
const MIN_STAGE_NODES = 20;
const POLL_MS = 60;
// A bundle that never reports ready is shown anyway — better its own loading
// text than a placeholder forever.
const GIVE_UP_MS = 12000;

function stageIsUp(frame: HTMLIFrameElement): boolean {
  try {
    const doc = frame.contentDocument;
    if (!doc || !doc.body) return false;
    return !doc.getElementById(LOADER_ID) && doc.body.getElementsByTagName('*').length > MIN_STAGE_NODES;
  } catch {
    // Not same-origin (e.g. served from elsewhere): nothing to inspect.
    return true;
  }
}

export default function JourneyAnimation({ className = '' }: { className?: string }) {
  const frameRef = useRef<HTMLDivElement>(null);
  const [scale, setScale] = useState(0);
  const [ready, setReady] = useState(false);
  const pollRef = useRef<number>();

  useEffect(() => {
    const el = frameRef.current;
    if (!el) return undefined;
    const fit = () => setScale(el.clientWidth / STAGE_W);
    fit();
    if (typeof ResizeObserver !== 'function') return undefined;
    const ro = new ResizeObserver(fit);
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  // Poll from the iframe's `load` (the bundle's document parsed) until its
  // loader has swapped in the stage. The bundle gives no event for that.
  const watchForStage = useCallback((e: React.SyntheticEvent<HTMLIFrameElement>) => {
    const frame = e.currentTarget;
    const started = Date.now();
    window.clearInterval(pollRef.current);
    pollRef.current = window.setInterval(() => {
      if (stageIsUp(frame) || Date.now() - started > GIVE_UP_MS) {
        window.clearInterval(pollRef.current);
        setReady(true);
      }
    }, POLL_MS);
  }, []);

  useEffect(() => () => window.clearInterval(pollRef.current), []);

  return (
    <figure className={className}>
      {/* The title above the frame, in brass — under it, faint, it read as a
          footnote and went unseen (owner, 2026-10-17). */}
      <figcaption className="mb-3 flex items-center gap-3 font-plex text-[12px] font-semibold tracking-[0.28em] text-brass">
        <span aria-hidden="true" className="h-px w-8 bg-brass" />
        THE CRIMINAL PRACTICE JOURNEY · FIR TO HEARING
      </figcaption>
      <div
        ref={frameRef}
        className="relative w-full aspect-video overflow-hidden border border-brass-hairline-soft bg-black/40 shadow-[0_24px_60px_-20px_rgba(0,0,0,0.75)]"
      >
        {/* Held behind the iframe until the stage is up. Not a spinner: the
            journey's own rail drawing from FIR toward hearing, its stages
            lighting as the line reaches them, so the wait already looks like
            the thing that is loading. Motion starts on first paint. */}
        <div
          aria-hidden="true"
          className={`absolute inset-0 bg-gradient-to-br from-black/30 via-black/10 to-black/30 transition-opacity duration-500 ${
            ready ? 'opacity-0' : 'opacity-100'
          }`}
        >
          <div className="absolute left-[12%] right-[12%] top-1/2 h-px origin-left animate-rail-draw bg-brass/70" />
          {STAGE_STOPS.map((left, i) => (
            <span
              key={left}
              className="absolute top-1/2 h-2.5 w-2.5 animate-stage-light rounded-full bg-brass shadow-[0_0_14px_rgba(201,162,39,0.55)]"
              style={{ left: `${left}%`, animationDelay: `${0.25 + i * 0.3}s` }}
            />
          ))}
          <span className="absolute left-[12%] top-[calc(50%+18px)] -translate-x-1/2 font-plex text-[10px] tracking-[0.28em] text-brass/70">
            FIR
          </span>
          <span className="absolute left-[88%] top-[calc(50%+18px)] -translate-x-1/2 font-plex text-[10px] tracking-[0.28em] text-brass/70">
            HEARING
          </span>
        </div>
        <iframe
          src={JOURNEY_SRC}
          title="iAdvocate criminal practice journey — from client and FIR to the hearing"
          onLoad={watchForStage}
          tabIndex={-1}
          className={`absolute top-0 left-0 border-0 origin-top-left transition-opacity duration-500 ${
            ready ? 'opacity-100' : 'opacity-0'
          }`}
          style={{
            width: STAGE_W,
            height: STAGE_H,
            transform: `scale(${scale})`,
            visibility: scale > 0 ? 'visible' : 'hidden',
          }}
        />
      </div>
    </figure>
  );
}
